import DOMPurify from 'dompurify';
import { resolveImageSources } from './emailImages';

/**
 * Sanitize HTML content for safe rendering.
 *
 * Images are allowed, but only the user's own (docs/EMAIL_IMAGES.md). Before this existed `img`
 * was stripped wholesale, which is the only reason no received mail has ever loaded a remote
 * image here — and a tracking pixel in a received mail is exactly what a remote `<img>` is. So
 * the rule is: a library image, referenced as `cid:bmb-<id>` and resolved to a blob URL, renders;
 * every other image becomes a non-loading placeholder, so the reader can still see that
 * something was there.
 *
 * The URI allowlist does the enforcing — it has never permitted `cid:`, and still does not permit
 * `data:` — so an `https:` or `data:` source cannot survive even if the hook below is changed.
 */
export function sanitizeHtml(html: string): string {
  // cid: references → blob URLs. Synchronous: the library is pre-warmed at app start.
  const resolved = resolveImageSources(html);

  const clean = DOMPurify.sanitize(resolved, {
    ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'hr', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img'],
    ALLOWED_ATTR: ['href', 'rel', 'src', 'alt', 'width', 'height'],
    FORBID_ATTR: ['style', 'target'],
    // blob: is added for our own resolved images; cid: and data: stay out, so an unresolved
    // reference and a pasted data-URI both lose their src and are replaced below.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|sms|callto|xmpp|blob):|[^a-z]|[a-z+-]+(?:[^a-z+:-]|$))/i,
  });

  const doc = new DOMParser().parseFromString(clean, 'text/html');

  // Force rel="noopener noreferrer" on every link to prevent tab-napping
  doc.querySelectorAll('a').forEach((a) => {
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute('target', '_blank');
  });

  // Any image that is not one of ours — a remote image from received mail, or a reference whose
  // blob never arrived — is replaced rather than loaded. `blob:` URLs are same-origin and
  // created by us; nothing else gets to make a request.
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') ?? '';
    if (src.startsWith('blob:')) return;
    const placeholder = doc.createElement('span');
    placeholder.className = 'email-image-placeholder';
    const alt = (img.getAttribute('alt') ?? '').trim();
    placeholder.textContent = alt ? `🖼 ${alt}` : '🖼 image';
    img.replaceWith(placeholder);
  });

  return doc.body.innerHTML;
}
